import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Bell, Headset, Sparkles, Upload } from 'lucide-react';
import { CsvImportModal } from '@/components/csv-import-modal';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useSupport } from '@/lib/support-context';

function invalidateWorkspaceData(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
  void qc.invalidateQueries({ queryKey: ['agreements'] });
  void qc.invalidateQueries({ queryKey: ['contacts'] });
  void qc.invalidateQueries({ queryKey: ['risk'] });
  void qc.invalidateQueries({ queryKey: ['campaigns'] });
  void qc.invalidateQueries({ queryKey: ['insights'] });
  void qc.invalidateQueries({ queryKey: ['upgrades'] });
  void qc.invalidateQueries({ queryKey: ['opportunities'] });
}

export function AppShellBanner() {
  const { user, usage, refresh } = useAuth();
  const { openSupport } = useSupport();
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  const agreementCount = usage?.agreements?.used ?? 0;
  const needsData = agreementCount === 0;
  const needsVerify = !needsData && !user?.user?.emailVerified;

  const seedDemo = useMutation({
    mutationFn: () => api('/api/v1/demo-data/seed', { method: 'POST', body: '{}' }),
    onSuccess: async () => {
      setSeedError(null);
      await refresh();
      invalidateWorkspaceData(qc);
    },
    onError: (err: Error) => setSeedError(err.message),
  });

  const onImportDone = async () => {
    await refresh();
    invalidateWorkspaceData(qc);
  };

  if (!needsData && !needsVerify) {
    return null;
  }

  if (needsData) {
    return (
      <>
        <div className="border-b border-brand/20 bg-brand-subtle/40 px-4 py-3 md:px-8">
          <div className="mx-auto flex max-w-[1200px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">Get your renewal dashboard started</p>
              <p className="mt-0.5 text-[13px] text-text-secondary">
                Import your member list, explore with sample customers (remove in one click), connect your field service
                app in Settings, or message us — we&apos;ll import it for you.
              </p>
              {seedError && <p className="mt-1.5 text-[13px] text-danger">{seedError}</p>}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" /> Import spreadsheet
              </Button>
              <Button size="sm" variant="secondary" loading={seedDemo.isPending} onClick={() => seedDemo.mutate()}>
                <Sparkles className="h-4 w-4" /> Load sample data
              </Button>
              <Button size="sm" onClick={() => openSupport('import_help')}>
                <Headset className="h-4 w-4" /> Get help
              </Button>
            </div>
          </div>
        </div>
        <CsvImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onDone={onImportDone}
          defaultKind="agreements"
          allowedKinds={['agreements', 'customers', 'services', 'equipment']}
        />
      </>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 border-b border-[#FDE68A] bg-warning-subtle px-4 py-2.5 text-center text-[13px] font-medium text-[#854D0E]">
      <Bell className="h-4 w-4 shrink-0" />
      <span>Verify your email to start sending live reminders — check your inbox.</span>
    </div>
  );
}
